import React, { useMemo, useState } from 'react'
import { X, Split as SplitIcon } from 'lucide-react'
import { projectsApi, SplitProjectSystemError, ProjectSystem } from '@/lib/projectsApi'
import { useToast } from '@/contexts/ToastContext'
import { Button } from '@/components/ui/Button'

interface Props {
  // the original project_system whose row is being split. carries
  // system_name + vendor for the header line
  original: ProjectSystem
  // the Word RA assessment that lands on the clone. drives the pre-fill
  // heuristic for the new system name
  assessment: {
    id:          string
    system_name: string
    supplier:    string | null
  }
  // name of whatever assessment already owns the original. server-side and
  // FE-side conflict messages both quote this, so the user sees the same
  // wording in the modal as in the toast
  conflictHolderName: string | null
  onClose:   () => void
  // fired on a successful split with the new clone row, so the caller can
  // refresh its listings and dispatch the cross-tab ra-mappings-updated
  // event (same pattern as accept)
  onSplit:   (clone: ProjectSystem) => void
}

// derive a sensible default for the new system name. the Word RA
// assessment name is verbose (e.g. "RADAR System (X-Band Radar)" or
// "Gas Detection and Sampling System"). we want a label that reads
// "<original> (<distinguisher>)" where distinguisher comes from the Word
// row. heuristic:
//   1. if the assessment name has a parenthetical, use the contents of
//      the LAST one. usually the discriminator the author intended
//      ("X-Band Radar")
//   2. otherwise use the full assessment name, uppercased to match the
//      Excel naming convention seen in SN2729 ("RADAR PLANT")
// output shape: "<original.system_name> (<distinguisher>)"
function deriveDefaultName(originalName: string | null, assessmentName: string): string {
  const orig = (originalName ?? '').trim() || 'Split system'
  const trimmed = assessmentName.trim()
  const parens = trimmed.match(/\(([^()]+)\)\s*$/)
  const distinguisher = parens ? parens[1].trim() : trimmed
  return `${orig} (${distinguisher.toUpperCase()})`
}

export const SplitSuggestionModal: React.FC<Props> = ({
  original, assessment, conflictHolderName, onClose, onSplit,
}) => {
  const { success, error: showError } = useToast()
  const defaultName = useMemo(
    () => deriveDefaultName(original.system_name, assessment.system_name),
    [original.system_name, assessment.system_name],
  )
  const [name,       setName]       = useState(defaultName)
  const [busy,       setBusy]       = useState(false)
  const [fieldError, setFieldError] = useState<string | null>(null)

  const trimmed = name.trim()
  const canSubmit = !busy && trimmed.length > 0 && trimmed !== (original.system_name ?? '').trim()

  const handleSubmit = async () => {
    if (!canSubmit) return
    setFieldError(null)
    setBusy(true)
    try {
      const clone = await projectsApi.splitProjectSystem(original.id, {
        new_system_name: trimmed,
        assessment_id:   assessment.id,
      })
      success(`Split: "${clone.system_name}" created and mapped`)
      onSplit(clone)
    } catch (err) {
      const e = err as SplitProjectSystemError
      if (e.code === 'NAME_TAKEN') {
        // inline field error so the user can fix the input without losing
        // typed context. don't close the modal
        setFieldError(e.message)
        setBusy(false)
        return
      }
      // ALREADY_MAPPED / CROSS_PROJECT / other: surface as toast and close.
      // the modal can't recover
      showError(e.message ?? 'Failed to split system')
      onClose()
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-md w-full overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-base font-semibold flex items-center gap-2">
            <SplitIcon className="h-4 w-4 text-rose-500" />
            Split into a new system
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5 space-y-4 text-sm">
          <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
            <span className="font-semibold">{original.system_name ?? '(unnamed)'}</span>
            {conflictHolderName && (
              <>
                {' '}is already mapped to{' '}
                <span className="font-semibold">&ldquo;{conflictHolderName}&rdquo;</span>
              </>
            )}.
            Create a separate inventory row so{' '}
            <span className="font-semibold">{assessment.system_name}</span>{' '}
            can land on its own copy of this entry.
          </p>

          <div>
            <label className="block text-[11px] font-medium text-slate-600 dark:text-slate-300 mb-1">
              New system name
            </label>
            <input
              autoFocus
              value={name}
              onChange={e => { setName(e.target.value); setFieldError(null) }}
              onKeyDown={e => { if (e.key === 'Enter' && canSubmit) handleSubmit() }}
              className={`w-full text-sm px-3 py-2 rounded border bg-white dark:bg-slate-900 text-slate-900 dark:text-white
                ${fieldError
                  ? 'border-rose-400 dark:border-rose-500'
                  : 'border-slate-300 dark:border-slate-600'}`}
              placeholder="e.g. RADAR PLANT (X-BAND)"
            />
            {fieldError ? (
              <p className="text-[11px] text-rose-500 mt-1">{fieldError}</p>
            ) : (
              <p className="text-[11px] text-slate-500 mt-1">
                Must differ from the original. Inventory data (VAI, contacts, assessments) stays with the original.
              </p>
            )}
          </div>
        </div>

        <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-700 flex items-center justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button size="sm" onClick={handleSubmit} disabled={!canSubmit}>
            {busy ? 'Splitting…' : 'Split'}
          </Button>
        </div>
      </div>
    </div>
  )
}
